import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { formatDateTime } from '../../utils/dateUtils';

const DeviceActivity = forwardRef(({ deviceId, onRefresh }, ref) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const renderDescription = (text) => {
    if (!text) return null;
    const parts = text.split(/(repair #\d+)/gi);
    return parts.map((part, i) => {
      const match = part.match(/^repair #(\d+)$/i);
      if (match) {
        return (
          <a
            key={i}
            href={`/repairs/${match[1]}/view`}
            onClick={(e) => { e.preventDefault(); navigate(`/repairs/${match[1]}/view`); }}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {part}
          </a>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  const fetchHistory = useCallback(async () => {
    if (!deviceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch device history
      const historyResponse = await axios.get(`/api/devices/${deviceId}/history`);
      
      const historyData = Array.isArray(historyResponse.data) ? historyResponse.data : [];
      const sortedHistory = historyData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setHistory(sortedHistory);
      setError('');
      
      // Call onRefresh callback if provided
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error fetching device activity:', err);
      setError('Failed to load device activity');
    } finally {
      setLoading(false);
    }
  }, [deviceId, onRefresh]);

  useEffect(() => {
    if (deviceId) {
      fetchHistory();
    }
  }, [deviceId, fetchHistory]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchHistory
  }));

  // Get status color and icon based on event type
  const getEventDisplay = (event) => {
    if (event.event_type === 'comment') {
      return {
        color: 'bg-blue-400',
        icon: <MessageCircle className="h-3 w-3 text-white" />
      };
    }
    
    if (event.event_type === 'device_created') {
      return { color: 'bg-green-400', icon: null };
    }
    
    if (event.event_type === 'device_linked_to_repair') {
      return { color: 'bg-yellow-500', icon: null };
    }
    
    if (event.event_type === 'device_linked_to_order') {
      return { color: 'bg-blue-500', icon: null };
    }
    
    if (event.event_type === 'device_unlinked_from_order') {
      return { color: 'bg-orange-500', icon: null };
    }
    
    // Ownership events from the SeeYou Cloud ownership sync
    if (event.event_type === 'owner_registered') {
      return { color: 'bg-green-500', icon: null };
    }

    if (event.event_type === 'owner_transfer') {
      return { color: 'bg-purple-400', icon: null };
    }

    if (event.event_type === 'owner_removed') {
      return { color: 'bg-orange-400', icon: null };
    }

    if (event.event_type === 'status_change') {
      const status = event.new_value;
      switch (status) {
        case 'active':
          return { color: 'bg-green-400', icon: null };
        case 'blocked':
          return { color: 'bg-red-400', icon: null };
        case 'draft':
          return { color: 'bg-gray-400', icon: null };
        case 'lost':
          return { color: 'bg-orange-400', icon: null };
        case 'retired':
          return { color: 'bg-purple-400', icon: null };
        case 'demo':
          return { color: 'bg-blue-400', icon: null };
        case 'sponsored':
          return { color: 'bg-indigo-400', icon: null };
        default:
          return { color: 'bg-gray-400', icon: null };
      }
    }
    
    // Default for other event types
    return { color: 'bg-gray-400', icon: null };
  };

  if (loading) {
    return (
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Device Activity</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading activity...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Device Activity</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Device Activity</h3>
        
      {/* Activity history */}
      {history.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No activity yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Device events will appear here as they occur.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical timeline line - centered on the dots */}
            <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-300"></div>
            
            {/* Timeline events */}
            <div className="space-y-4">
              {history.map((event, index) => {
                const eventDisplay = getEventDisplay(event);
                const isComment = event.event_type === 'comment';
                
                return (
                  <div key={event.id || index} className="relative flex items-start">
                    {/* Status dot - 24px wide (w-6), so center is at 12px */}
                    <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white ${eventDisplay.color}`}>
                      {eventDisplay.icon}
                    </div>
                    
                    {/* Event content */}
                    <div className="ml-4 min-w-0 flex-1">
                      <div className="text-sm text-gray-900">
                        <span className="font-medium text-gray-500">
                          {formatDateTime(event.timestamp)}
                        </span>
                        <span className="mx-2 text-gray-400">-</span>
                        {event.performed_by_name ? (
                          <>
                            <span className="font-medium text-gray-900">
                              {event.performed_by_name}
                            </span>
                            {isComment ? (
                              <span className="text-gray-700"> commented:</span>
                            ) : (
                              <span className="text-gray-700">
                                {' '}{renderDescription(event.event_description)}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-700">
                            {renderDescription(event.event_description)}
                          </span>
                        )}
                      </div>
                      
                      {/* Comment content - displayed separately for better formatting */}
                      {isComment && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-md border-l-4 border-blue-200">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {event.event_description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );
});

DeviceActivity.displayName = 'DeviceActivity';

export default DeviceActivity;
